import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import { printerStore } from "../../stores/print_mapping/printer_store"
import { areaStore } from "../../stores/locales/area_store"

/**
 * Manages printer selection panel for the visual print mapper.
 * - Fetches printers when an area is selected (via areaStore)
 * - Renders a scrollable list of printers (capped at 50)
 * - Highlights the selected printer and updates printerStore
 * - Renders selected printer icon on the floor plan
 * - Clears selection and list when area changes
 */
export default class extends Controller {
    static stores = [printerStore, areaStore]
    static targets = ["printerList", "emptyState"]

    connect() {
        // Initialize instance state BEFORE useStore(this): useStore synchronously fires
        // onAreaStoreUpdate with the store's current value during connect(), and that
        // callback calls _removeAllPrinterIcons() which reads this._printerIconEls — so
        // the Map must already exist or connect() throws and the controller (and the
        // rest of the VPM page) fails to boot.
        this._printers = []
        // Ids of currently-rendered printer floor-plan icons, keyed by printer id ->
        // element, so we can restyle (highlight/unhighlight) and clean them up.
        this._printerIconEls = new Map()
        // Id of the printer currently hover-previewed from the list (or null).
        this._hoveredPrinterId = null

        useStore(this)
    }

    disconnect() {
        this._removeAllPrinterIcons()
    }

    // Store callbacks

    onAreaStoreUpdate(area) {
        // Clear current selection and all printer icons on the floor plan.
        this.setPrinterStoreValue({})
        this._removeAllPrinterIcons()
        this._printers = []

        if (area && area.id) {
            this._fetchPrinters(area.id)
        } else {
            this._renderEmptyState('Select an area to view printers')
        }
    }

    // Actions

    selectPrinter(event) {
        const item = event.currentTarget
        const index = parseInt(item.dataset.printerIndex, 10)
        const printer = this._printers[index]

        if (!printer) return

        // Remove highlight from previously selected
        if (this.hasPrinterListTarget) {
            this.printerListTarget.querySelectorAll('.printer-list-item').forEach(el => {
                el.classList.remove('active')
            })
        }

        // Highlight selected item
        item.classList.add('active')

        // Update printer store
        this.setPrinterStoreValue(printer)

        // Restyle all printer icons so the selected one is highlighted and the rest
        // stay black. The connection-lines controller anchors to the icon carrying
        // data-selected-printer="true"; only the highlighted icon carries it.
        this._highlightSelectedPrinterIcon(printer.id)
    }

    // Hovering a printer in the list previews it on the floor plan with a distinct
    // hover style (a blue ring), so the user can locate a printer without selecting
    // it. Skips the currently-selected printer so its gold highlight is not disturbed.
    hoverPrinter(event) {
        const printerId = event.currentTarget.dataset.printerId
        if (!printerId) return

        this._hoveredPrinterId = printerId
        const iconEl = this._printerIconEls.get(printerId)
        if (!iconEl) return
        // Do not override the selected printer's highlight.
        if (iconEl.dataset.selectedPrinter === 'true') return

        this._applyPrinterIconHoverStyle(iconEl)
    }

    // Ends the hover preview: restore the icon to its correct base style (the gold
    // selected style if it is the selected printer, otherwise plain black).
    unhoverPrinter(event) {
        const printerId = event.currentTarget.dataset.printerId
        if (!printerId) return

        if (this._hoveredPrinterId === printerId) this._hoveredPrinterId = null
        const iconEl = this._printerIconEls.get(printerId)
        if (!iconEl) return
        // The selected printer keeps its gold highlight; it was never given the hover
        // style, so there is nothing to restore for it.
        if (iconEl.dataset.selectedPrinter === 'true') return

        this._applyPrinterIconStyle(iconEl, false)
    }

    // Private - Printer icons on floor plan

    // Renders every printer in the area as a black icon on the floor plan. Called
    // after the printer list is fetched. Non-selected printers are plain black; the
    // selected printer is later highlighted via _highlightSelectedPrinterIcon.
    _renderAllPrinterIconsOnFloorPlan() {
        this._removeAllPrinterIcons()

        const deviceList = document.querySelector('.existing-devices-container[data-floorplans--floor-plan-icon-manager-target="deviceList"]')
        if (!deviceList) return

        this._printers.forEach(printer => {
            const x = parseFloat(printer.floorplan_x_coordinate)
            const y = parseFloat(printer.floorplan_y_coordinate)
            if (isNaN(x) || isNaN(y)) return

            const iconEl = document.createElement('div')
            iconEl.id = `printer-selection-${printer.id}`
            iconEl.style.position = 'absolute'
            iconEl.style.top = `${y}px`
            iconEl.style.left = `${x}px`
            iconEl.style.width = '30px'
            iconEl.style.height = '30px'
            iconEl.style.display = 'block'
            iconEl.style.textAlign = 'center'
            iconEl.style.lineHeight = '30px'
            iconEl.style.zIndex = '20'
            // Mark as a printer icon (all printers) with its id. The selected printer
            // additionally gets data-selected-printer="true" in the highlight step —
            // the connection-lines controller anchors to that selected marker.
            iconEl.dataset.printerIcon = 'true'
            iconEl.dataset.printerId = printer.id
            iconEl.dataset.deviceTypeIcon = printer.device_type_icon || 'fa fa-print'

            this._applyPrinterIconStyle(iconEl, false)

            deviceList.appendChild(iconEl)
            this._printerIconEls.set(printer.id, iconEl)
        })
    }

    // Re-styles all rendered printer icons so the given id is highlighted and every
    // other printer stays plain black. Also maintains the data-selected-printer
    // marker that the connection-lines controller uses to anchor lines.
    _highlightSelectedPrinterIcon(selectedId) {
        // If the floor-plan icons were not rendered yet (e.g. the printers fetch
        // resolved before the icon manager created the deviceList container), render
        // them now so there is a selected icon for the connection-lines controller to
        // anchor to.
        if (!this._printerIconEls.has(selectedId)) {
            this._renderAllPrinterIconsOnFloorPlan()
        }

        this._printerIconEls.forEach((iconEl, printerId) => {
            const isSelected = printerId === selectedId
            if (isSelected) {
                iconEl.dataset.selectedPrinter = 'true'
            } else {
                delete iconEl.dataset.selectedPrinter
            }
            this._applyPrinterIconStyle(iconEl, isSelected)
        })
    }

    // Applies the badge markup for a printer icon. Non-selected printers render as a
    // black badge with a white glyph; the selected printer is highlighted with a
    // gold/yellow badge (matching the connection line) plus a subtle ring so it
    // clearly stands out from the black ones.
    _applyPrinterIconStyle(iconEl, isSelected) {
        const iconClass = (iconEl.dataset.deviceTypeIcon || 'fa fa-print').replace(/\bfas\b/g, 'fa')
        if (isSelected) {
            iconEl.innerHTML = `<span class="badge badge-pill" style="background-color: #ffc107; box-shadow: 0 0 0 2px #000000; transform: scale(1.15); display: inline-block;"><i class="${iconClass}" style="font-size: 16px; color: #000000;"></i></span>`
        } else {
            iconEl.innerHTML = `<span class="badge badge-pill" style="background-color: #000000;"><i class="${iconClass}" style="font-size: 16px; color: white;"></i></span>`
        }
    }

    // Hover-preview style for a (non-selected) printer icon. Distinct from both the
    // plain-black base and the gold selected style: a black badge with a blue ring and
    // a slight scale-up, so hovering the list clearly points out the printer on the
    // floor plan without implying it is selected.
    _applyPrinterIconHoverStyle(iconEl) {
        const iconClass = (iconEl.dataset.deviceTypeIcon || 'fa fa-print').replace(/\bfas\b/g, 'fa')
        iconEl.innerHTML = `<span class="badge badge-pill" style="background-color: #000000; box-shadow: 0 0 0 2px #0d6efd; transform: scale(1.1); display: inline-block;"><i class="${iconClass}" style="font-size: 16px; color: white;"></i></span>`
    }

    _removeAllPrinterIcons() {
        this._printerIconEls.forEach(el => el.remove())
        this._printerIconEls.clear()
        // Belt-and-suspenders: remove any stray printer/selected-printer icons that
        // may have been left behind (e.g. across a re-render), so no stale icon can
        // mislead the connection-lines anchor lookup.
        document.querySelectorAll('[data-printer-icon="true"], [data-selected-printer="true"]').forEach(el => el.remove())
    }

    // Private - Fetch and render

    async _fetchPrinters(areaId) {
        this._renderLoading()

        try {
            const response = await fetch(`/api/print_mappings/printers?area_id=${encodeURIComponent(areaId)}`)
            if (!response.ok) {
                console.error('Failed to fetch printers')
                this._renderEmptyState('Failed to load printers')
                return
            }

            const data = await response.json()
            // Sort printers alphabetically by name (case-insensitive, natural order)
            // before capping the list so the panel is easy to scan.
            this._printers = (data.printers || [])
                .slice()
                .sort((a, b) => (a.name || '').localeCompare((b.name || ''), undefined, { numeric: true, sensitivity: 'base' }))
                .slice(0, 50)
            this._renderPrinterList()
            // Show ALL printers as black icons on the floor plan. Selecting one later
            // highlights it (see _highlightSelectedPrinterIcon) without hiding the rest.
            this._renderAllPrinterIconsOnFloorPlan()
        } catch (error) {
            console.error('Error fetching printers:', error)
            this._renderEmptyState('Error loading printers')
        }
    }

    _renderPrinterList() {
        if (!this.hasPrinterListTarget) return

        if (this._printers.length === 0) {
            this._renderEmptyState('No printers available in this area')
            return
        }

        this._hideEmptyState()

        let html = ''
        this._printers.forEach((printer, index) => {
            const typeName = printer.device_type_name
                ? `<span class="text-muted small">${this._escapeHtml(printer.device_type_name)}</span>`
                : ''
            html += `<div class="printer-list-item list-group-item list-group-item-action d-flex flex-column align-items-start"
                          data-action="click->print-mapper--printer-selection#selectPrinter mouseenter->print-mapper--printer-selection#hoverPrinter mouseleave->print-mapper--printer-selection#unhoverPrinter"
                          data-printer-index="${index}"
                          data-printer-id="${this._escapeHtml(printer.id)}"
                          role="button">
                <span class="fw-medium">${this._escapeHtml(printer.name)}</span>
                ${typeName}
            </div>`
        })

        this.printerListTarget.innerHTML = html
    }

    _renderEmptyState(message) {
        if (this.hasPrinterListTarget) {
            this.printerListTarget.innerHTML = ''
        }
        if (this.hasEmptyStateTarget) {
            this.emptyStateTarget.textContent = message
            this.emptyStateTarget.classList.remove('d-none')
        }
    }

    _hideEmptyState() {
        if (this.hasEmptyStateTarget) {
            this.emptyStateTarget.classList.add('d-none')
        }
    }

    _renderLoading() {
        if (this.hasPrinterListTarget) {
            this.printerListTarget.innerHTML = '<div class="text-center p-3"><div class="spinner-border spinner-border-sm" role="status"><span class="visually-hidden">Loading...</span></div></div>'
        }
        this._hideEmptyState()
    }

    _escapeHtml(str) {
        if (!str) return ''
        const div = document.createElement('div')
        div.textContent = str
        return div.innerHTML
    }
}
